import { useCallback, useRef } from 'react';

import { useTranslation } from 'react-i18next';

import { profileIdDellaSfida } from '@/lib/limulo/challenge';
import { degradedCopyKeys } from '@/lib/limulo/gating';
import { dbg } from '@/lib/logto';
import {
  creaEsincronizzaProfilo,
  esitoSyncRegistrato,
  motivoDelRifiuto,
  pianoProfiloImplicito,
  type EsitoSyncAlTocco,
  type ProfiloPerIscrizione,
} from '@/lib/limulo/registration';
import {
  useCreateLimuloProfileMutation,
  useLazyGetLimuloGameProfileRequirementsQuery,
  useLazyGetLimuloProfilesQuery,
  usePostLimuloProfileSyncMutation,
} from '@/services/limuloApi';

/**
 * **Il profilo di gioco di chi sfida o accetta, creato al tocco quando lo schema non chiede niente** (I-493, I-543:
 * Martha non poteva accettare la sfida di Luca perche' non aveva il profilo scacchi, e un gioco a zero campi il
 * profilo lo faceva nascere solo l'iscrizione a un torneo).
 *
 * La regola e' UNA, quella dell'iscrizione ai tornei (`pianoProfiloImplicito`): decide lo SCHEMA del provider, mai il
 * gioco. Nessun profilo per il gioco E schema a zero campi ⇒ lo si crea (stesso passo dell'iscrizione e dell'«Entra»
 * di un sit&go: `creaEsincronizzaProfilo`, cioe' `createProfileReconciled` + il sync atteso col suo tetto) e si
 * prosegue col `profileId` appena nato. Con campi (Clash Royale: tag e token) niente si crea: la sfida parte com'e'
 * oggi e il motore risponde `sfida.profilo-richiesto`.
 *
 * ⚠️ Si chiama al TOCCO dell'utente (Invia, Accetta), mai all'apertura del foglio ne' della riga: e' una scrittura
 * fatta per suo conto, e solo il suo gesto la giustifica. Anche lo schema si legge al tocco (`useLazy…`, la voce di
 * cache e' la stessa di `useSyncProfiloLimulo`): chi ha gia' un profilo non paga neanche quella lettura.
 * ⚠️ Il `profileId` della sfida e' quello della CREAZIONE, passato da una chiamata all'altra: la lista di questo
 * render dice «nessuno» per costruzione, e rileggerla sarebbe la corsa di #375 a parti invertite.
 * ⚠️ La guardia contro il doppio tocco e' una REF (`inCorso`), non uno stato: `setState` non ha effetto sulla riga
 * dopo, e due tocchi nello stesso tick creerebbero due volte il profilo. Il secondo si aggancia alla promessa del primo.
 */

/** Il profilo c'e' (o non serve crearlo): si prosegue. `profileId` e' `null` solo dove il motore dara' il suo rifiuto. */
export type EsitoProfiloPerSfida =
  | { kind: 'pronto'; profileId: string | null }
  /** Nessun profilo e lo schema non si legge: non si crea niente su una risposta che non c'e'. */
  | { kind: 'schema-illeggibile' }
  /** La creazione e' stata rifiutata: NIENTE sfida. `ragione` e' gia' una frase (il motivo del provider, se c'e'). */
  | { kind: 'non-creato'; ragione: string }
  /** Il profilo risulta creato ma non ne e' arrivato l'id: niente sfida nuda, il tocco dopo lo trova in lista. */
  | { kind: 'senza-id' }
  /** kb#388: il profilo c'e' ma il sync ATTESO dalla creazione non e' andato (403 ⇒ `identita`, altro ⇒ `non-risponde`): NIENTE sfida. */
  | { kind: 'collegamento'; esito: Exclude<EsitoSyncAlTocco, { kind: 'ok' }> };

/**
 * Serve creare un profilo? Solo senza profilo per il gioco (la lista c'e': `nessuno`, mai `sconosciuto`) e con un gioco.
 * E' la meta' SINCRONA della decisione: dice se il tocco puo' proseguire subito (come prima di I-543) o deve passare
 * da `prepara`. L'altra meta' (lo schema) si sa solo leggendolo, e si legge in `prepara`.
 */
export function serveCrearloProfilo(scelta: ProfiloPerIscrizione, gameId: string): boolean {
  return scelta.kind === 'nessuno' && gameId.trim() !== '';
}

export function useProfiloPerSfida() {
  const { t: tLimulo } = useTranslation('limulo');
  const [createProfile] = useCreateLimuloProfileMutation();
  const [syncProfile] = usePostLimuloProfileSyncMutation();
  // Lazy: la rilettura dei profili parte solo dopo un rifiuto della creazione (`createProfileReconciled`).
  const [readProfiles] = useLazyGetLimuloProfilesQuery();
  const [leggiSchema] = useLazyGetLimuloGameProfileRequirementsQuery();
  const inCorso = useRef<Promise<EsitoProfiloPerSfida> | null>(null);

  const eseguiPrepara = useCallback(
    async (gameId: string, scelta: ProfiloPerIscrizione): Promise<EsitoProfiloPerSfida> => {
      if (!serveCrearloProfilo(scelta, gameId)) return { kind: 'pronto', profileId: profileIdDellaSfida(scelta) };

      // `preferCacheValue`: si aggancia alla lettura in volo o a quella in cache, non ne apre una nuova.
      const letto = await leggiSchema(gameId, true);
      const piano = pianoProfiloImplicito({
        scelta,
        gameId,
        requisitiGioco: letto.data,
        inErrore: letto.data === undefined,
      });
      if (piano === 'in-lettura' || piano === 'illeggibile') return { kind: 'schema-illeggibile' };
      // `modulo`: lo schema chiede dei dati all'utente, e la TMA non li inventa. La sfida parte com'e' oggi.
      if (piano !== 'implicito') return { kind: 'pronto', profileId: null };

      const creato = await creaEsincronizzaProfilo({
        createProfile: (b) => createProfile(b as { gameId: string } & Record<string, string>),
        readProfiles: () => readProfiles({ activeOnly: false }),
        syncProfile: (id) => syncProfile(id),
        log: dbg,
        gameId,
      });
      if (creato.fase === 'profilo-senza-id') return { kind: 'senza-id' };
      if (creato.fase === 'profilo') {
        const rifiuto = creato.profilo.failure;
        return {
          kind: 'non-creato',
          ragione: rifiuto
            ? motivoDelRifiuto(rifiuto, tLimulo(degradedCopyKeys(rifiuto.verdict).titleKey))
            : tLimulo('degraded.unknown.title'),
        };
      }
      // kb#388: `creaEsincronizzaProfilo` ha ASPETTATO il sync e ne ha tenuto l'esito nel registro: un 403 o un guasto non si
      // perdono piu' in `dbg`, e la sfida non parte su un profilo che il provider non ha collegato.
      const sync = esitoSyncRegistrato(creato.profileId);
      if (sync.kind !== 'ok') return { kind: 'collegamento', esito: sync };
      return { kind: 'pronto', profileId: creato.profileId };
    },
    [createProfile, leggiSchema, readProfiles, syncProfile, tLimulo],
  );

  /** Non lancia mai: un guasto imprevisto e' «profilo non creato», con il suo nome, mai una sfida senza profilo. */
  const prepara = useCallback(
    (gameId: string, scelta: ProfiloPerIscrizione): Promise<EsitoProfiloPerSfida> => {
      if (inCorso.current !== null) return inCorso.current;
      const promessa = eseguiPrepara(gameId, scelta)
        .catch(
          (e: unknown): EsitoProfiloPerSfida => ({
            kind: 'non-creato',
            ragione: e instanceof Error ? e.name : tLimulo('degraded.unknown.title'),
          }),
        )
        .finally(() => {
          inCorso.current = null;
        });
      inCorso.current = promessa;
      return promessa;
    },
    [eseguiPrepara, tLimulo],
  );

  return prepara;
}
